import React, { useEffect, useState, Fragment } from 'react';
import { Users, CheckCircle, Clock, ChevronRight, ArrowLeft, Building2, UserCircle, Briefcase } from 'lucide-react';
import { Layout } from '../components/Layout';
import { StatCard } from '../components/StatCard';
import { LeadQueue } from '../components/LeadQueue';
import { useRole } from '../contexts/RoleContext';
import { USERS, MOCK_LEADS } from '../data/mockLeads';
import { motion, AnimatePresence } from 'framer-motion';
export function FollowUpQueue() {
  const {
    role
  } = useRole();
  // Default view based on role
  const defaultViewId = role === 'admin' ? 'company' : role === 'manager' ? 'manager1' : 'officer1';
  const [viewId, setViewId] = useState<string>(defaultViewId);
  // Reset view when role changes
  useEffect(() => {
    setViewId(role === 'admin' ? 'company' : role === 'manager' ? 'manager1' : 'officer1');
  }, [role]);
  const currentEntity = viewId === 'company' ? null : USERS.find(u => u.id === viewId);
  const currentRole = viewId === 'company' ? 'company' : currentEntity?.role;
  // Helpers to get leads for a specific view
  const getLeadsForView = (id: string) => {
    if (id === 'company') return MOCK_LEADS;
    const entity = USERS.find(u => u.id === id);
    if (entity?.role === 'manager') {
      // Manager's own leads + their team's leads
      const teamIds = USERS.filter(u => u.managerId === id).map(u => u.id);
      return MOCK_LEADS.filter(l => l.ownerId === id || teamIds.includes(l.ownerId));
    }
    // Officer's leads
    return MOCK_LEADS.filter(l => l.ownerId === id);
  };
  const currentLeads = getLeadsForView(viewId);
  const totalFollowUps = currentLeads.length;
  const completedFollowUps = currentLeads.filter(l => l.status === 'contacted').length;
  const pendingFollowUps = currentLeads.filter(l => l.status === 'pending').length;
  // Drill-down lists
  const managers = USERS.filter(u => u.role === 'manager');
  const teamMembers = currentEntity?.role === 'manager' ? USERS.filter(u => u.managerId === currentEntity.id) : [];
  // Build the trail of views from the role's top level down to the current view.
  const getCrumbs = () => {
    const crumbs: {
      id: string;
      label: string;
      icon: React.ReactNode;
    }[] = [];
    if (role === 'admin') {
      crumbs.push({
        id: 'company',
        label: 'Company Overview',
        icon: <Building2 size={14} data-id="element-433" data-mp="2zpftu3o8lfac" />
      });
      if (currentEntity) {
        if (currentEntity.role === 'officer') {
          const manager = USERS.find(u => u.id === currentEntity.managerId);
          if (manager) {
            crumbs.push({
              id: manager.id,
              label: `${manager.name}'s Team`,
              icon: <Briefcase size={14} data-id="element-434" data-mp="3dofhb800jo4x" />
            });
          }
        }
        crumbs.push({
          id: currentEntity.id,
          label: currentEntity.name,
          icon: <UserCircle size={14} data-id="element-435" data-mp="170yemderknpm" />
        });
      }
    } else if (role === 'manager') {
      crumbs.push({
        id: 'manager1',
        label: 'My Team',
        icon: <Briefcase size={14} data-id="element-436" data-mp="1202abyqi2zjo" />
      });
      if (currentEntity && currentEntity.role === 'officer') {
        crumbs.push({
          id: currentEntity.id,
          label: currentEntity.name,
          icon: <UserCircle size={14} data-id="element-437" data-mp="1bu17skfmpv5a" />
        });
      }
    }
    return crumbs;
  };
  const crumbs = getCrumbs();
  // The view one level up from the current drill-down (used for the Back button).
  const parentCrumb = crumbs.length > 1 ? crumbs[crumbs.length - 2] : null;
  const renderBackButton = () => {
    if (!parentCrumb) return null;
    return <button onClick={() => setViewId(parentCrumb.id)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 shadow-sm hover:border-blue-300 hover:text-blue-600 transition-colors" data-id="element-438" data-mp="t173ckfmtlfb">
        <ArrowLeft size={16} data-id="element-439" data-mp="dzrx6ribpxox" />
        Back to {parentCrumb.label}
      </button>;
  };
  const renderBreadcrumbs = () => {
    if (crumbs.length <= 1) return null;
    return <nav className="flex items-center gap-2 text-sm" data-id="element-440" data-mp="j0fsvqhhz2ph">
        {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return <Fragment key={crumb.id} data-id="element-441" data-mp="s0kqxa0swoqz" data-mpi={index}>
              {index > 0 && <ChevronRight size={14} className="text-slate-400" data-id="element-442" data-mp="wxb860ocdqdz" />}
              <button onClick={() => !isLast && setViewId(crumb.id)} disabled={isLast} className={`flex items-center gap-1.5 font-medium transition-colors ${isLast ? 'text-slate-900 cursor-default' : 'text-slate-500 hover:text-blue-600'}`} data-id="element-443" data-mp="11wnrwcjx2hcc">
                {crumb.icon}
                {crumb.label}
              </button>
            </Fragment>;
      })}
      </nav>;
  };
  const renderEntityCard = (entity: (typeof USERS)[0], subtitle: string) => {
    const leads = getLeadsForView(entity.id);
    const pending = leads.filter(l => l.status === 'pending').length;
    const completed = leads.filter(l => l.status === 'contacted').length;
    return <motion.button whileHover={{
      y: -2
    }} onClick={() => setViewId(entity.id)} className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md transition-all text-left w-full flex items-center justify-between group" data-id="element-444" data-mp="1ku7rtx2jr9jr">
        <div className="flex items-center gap-4" data-id="element-445" data-mp="28k21y6vhek0b">
          <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center text-lg font-semibold text-slate-700 uppercase" data-id="element-446" data-mp="23s20ub3odl25">
            {entity.name.substring(0, 2)}
          </div>
          <div data-id="element-447" data-mp="2w94jleq48cut">
            <h3 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors" data-id="element-448" data-mp="2w9wcghsbvwhe">
              {entity.name}
            </h3>
            <p className="text-sm text-slate-500" data-id="element-449" data-mp="2rgbdamf00wgm">{subtitle}</p>
          </div>
        </div>
        <div className="flex items-center gap-6" data-id="element-450" data-mp="pmyh69z3s21v">
          <div className="text-right hidden sm:block" data-id="element-451" data-mp="uinjgl2ibx1v">
            <div className="text-sm font-medium text-slate-900" data-id="element-452" data-mp="uljm2mpav9wn">{pending}</div>
            <div className="text-xs text-slate-500" data-id="element-453" data-mp="fuj1xkf6xrko">Pending</div>
          </div>
          <div className="text-right hidden sm:block" data-id="element-454" data-mp="ksb2f2twim0h">
            <div className="text-sm font-medium text-slate-900" data-id="element-455" data-mp="6ue2bwg8nxh2">
              {completed}
            </div>
            <div className="text-xs text-slate-500" data-id="element-456" data-mp="btqr4mqo7ema">Completed</div>
          </div>
          <ChevronRight size={20} className="text-slate-300 group-hover:text-blue-500 transition-colors" data-id="element-457" data-mp="3t1kpwprakec9" />
        </div>
      </motion.button>;
  };
  return <Layout data-id="element-458" data-mp="1r9y6b99qblpt">
      <div className="max-w-7xl mx-auto space-y-8" data-id="element-459" data-mp="1r9g29qgts37c">
        <div data-id="element-460" data-mp="1rar43kqpn0vt">
          {parentCrumb && <div className="mb-4" data-id="element-461" data-mp="1w92d5t96l7cz">{renderBackButton()}</div>}
          <h1 className="text-2xl font-bold text-slate-900" data-id="element-462" data-mp="1w8iyqmiqp13p">
            {viewId === 'company' ? 'Company Overview' : viewId === 'manager1' && role === 'manager' ? 'My Team & Queue' : currentEntity?.name + "'s Queue"}
          </h1>
          <p className="text-slate-500 mt-1" data-id="element-463" data-mp="3qihcgow38ob3">
            Manage daily lead communications and campaign follow-ups.
          </p>
          {crumbs.length > 1 && <div className="mt-3" data-id="element-464" data-mp="3bnly0ld3avs">{renderBreadcrumbs()}</div>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-465" data-mp="3vg9e4amjbosh">
          <StatCard title="Total Follow-ups Today" value={totalFollowUps} icon={<Users size={20} data-id="element-467" data-mp="d8a60n5v5xwr" />} trend={{
          value: '2 from yesterday',
          isPositive: true
        }} data-id="element-466" data-mp="3vi2bt0f66yoc" />
          <StatCard title="Completed" value={completedFollowUps} icon={<CheckCircle size={20} data-id="element-469" data-mp="2a3g5rln9eybx" />} className="border-emerald-100 bg-emerald-50/30" data-id="element-468" data-mp="2f0y64nn7o58b" />
          <StatCard title="Pending" value={pendingFollowUps} icon={<Clock size={20} data-id="element-471" data-mp="1t7sim49d7x4n" />} className="border-amber-100 bg-amber-50/30" data-id="element-470" data-mp="1o8fvrjihuq22" />
        </div>

        <AnimatePresence mode="wait" data-id="element-472" data-mp="1y0lk8j9jepci">
          <motion.div key={viewId} initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} transition={{
          duration: 0.2
        }} className="space-y-8" data-id="element-473" data-mp="1y1mw2pydnfva">
            {/* Admin viewing Company -> Show Managers */}
            {currentRole === 'company' && <div className="space-y-4" data-id="element-474" data-mp="2qo7wsh0qt4bi">
                <h2 className="text-lg font-semibold text-slate-900" data-id="element-475" data-mp="2qn6d5o5nv9i6">
                  Sales Managers
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-476" data-mp="2vp5dsqa0nsbf">
                  {managers.map(m => renderEntityCard(m, 'Sales Manager'))}
                </div>
              </div>}

            {/* Manager viewing their team -> Show Officers */}
            {currentRole === 'manager' && <div className="space-y-4" data-id="element-477" data-mp="5aszooqz0hyk">
                <h2 className="text-lg font-semibold text-slate-900" data-id="element-478" data-mp="59rpemhs3zhw">
                  Team Members
                </h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-id="element-479" data-mp="d9lfou9w56m">
                  {teamMembers.map(o => renderEntityCard(o, 'Loan Officer'))}
                </div>
              </div>}

            {/* Always show the queue for the current view level */}
            <div className="space-y-4" data-id="element-480" data-mp="3iicu1jjg6zbs">
              <h2 className="text-lg font-semibold text-slate-900" data-id="element-481" data-mp="3ijfnnz85nfve">
                {currentRole === 'company' ? 'Company-wide Queue' : currentRole === 'manager' ? 'My Personal Queue' : 'Follow-up Queue'}
              </h2>
              <LeadQueue ownerId={viewId} data-id="element-482" data-mp="1zilw5uuxj1pa" />
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </Layout>;
}